import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import {
  listAdmissions,
  listDischarges,
  getAdmission,
  attachBed,
  transferBed,
  changePrimaryDoctor,
  patchAdmission,
  transitionAdmission,
  updatePlanEstimate,
} from '../../services/api/admissionApi';
import {
  ADMISSION_STATUS_LABELS, DESK_BUCKET, DESK_BUCKET_LABELS,
  ADMISSION_PERMISSIONS, ADMISSION_ACCESS_ERROR_CODE,
} from '../../constants/admission';
import { ROUTES } from '../../constants/routes';
import {
  AssignBedDialog,
  ChangeDoctorDialog,
  AttenderDialog,
  PatientDetailsDialog,
  CancelAdmissionDialog,
  EditPlanEstimateDialog,
} from './deskDialogs';
import TransferBedDialog from './TransferBedDialog';
import OrderLabDialog from './OrderLabDialog';
import AdmissionRailItem from './AdmissionRailItem';
import DeptAccessBlockedPanel from './DeptAccessBlockedPanel';
import { useDeptAccessBlocked } from './hooks';
import AdmissionWorkspace from './AdmissionWorkspace';
import AdmissionDashboardPanel from './AdmissionDashboardPanel';
import AccrualRunBanner from './AccrualRunBanner';
import DeskFilterBar from './DeskFilterBar';
import SearchSuggestInput from './SearchSuggestInput';
import { useDoctorRoster } from './hooks';
import useAssignableProviders from '../../hooks/useAssignableProviders';
import ScopeCaption from '../common/ScopeCaption';
import { mapDoctorsResponse } from '../../services/api/visitApi';
import { STAFF_ROLES } from '../../constants/staff';
import { useAdmissionSearchIndex } from './useAdmissionSearchIndex';
import { useAdmissionServerSearch } from './useAdmissionServerSearch';
import {
  parseDeskParams,
  serializeDeskParams,
  applyDeskFilters,
  sortDeskRows,
  computeDeskCounts,
  matchSuggestions,
  mergeSuggestionRows,
  mergeSearchResults,
  patchAdmissionBalance,
  bucketPartitions,
  viewForStatus,
  defaultDeskFilters,
  anyPartitionTruncated,
  isDischargeWindowView,
  anchorDischargeWindow,
  withoutStaleDischargeWindow,
  filtersHidingPick,
  dischargeWindowLabel,
  dischargeWindowPhrase,
  dischargeSearchEmptyHint,
  withoutMoneyFilters,
  collectRowDoctors,
} from './deskFilters';
import { downloadAdmissionsCsv } from './deskExport';
import { PICK_IDLE, pickTransition, loadKeyOf } from './deskPick';
import { collectDeposit, writeOffDues, openAdmissionReceiptPdf } from '../../services/api/admissionPaymentsApi';
import { grantConcession } from '../../services/api/admissionChargesApi';
import { useRealtimeEvent } from '../../hooks/useRealtimeEvent';
import { usePermission } from '../../context/PermissionContext';
import { PAYMENT_PERMISSIONS, CHARGE_PERMISSIONS, DUES_PERMISSIONS, REPORT_PERMISSIONS } from '../../constants/admission';
import TakeDepositDialog from './bill/TakeDepositDialog';
import WaiverConcessionDialog from './bill/WaiverConcessionDialog';
import WriteOffDuesDialog from './bill/WriteOffDuesDialog';
import IpdDepositsReportDialog from './IpdDepositsReportDialog';
import AdminPageHeader from '../common/AdminPageHeader';
import NoAccessGate from '../common/NoAccessGate';
import NoAccessPanel, { NO_ACCESS_VARIANT } from '../common/NoAccessPanel';

// WS event (C-9) — `admission_balance_changed` emitted post-commit from the
// written row. The RealtimeSync layer routes it to this EventBus type; see the
// integration note (BE + RealtimeSyncService must map the server message).
export const ADMISSION_BALANCE_EVENT = 'ADMISSION_BALANCE_CHANGED';

// Rail pagination page size (R10 long-list) — the rail shows the first N and
// grows by N on "Show 20 more"; resets to N on any filter/search change.
const RAIL_PAGE_SIZE = 20;

// Deep link that opens one admission by id (`/org/admissions?admission=<id>`).
const OPEN_ADMISSION_PARAM = 'admission';

/**
 * AdmissionDeskPage — route entry. The whole desk sits behind the Manage
 * Admission gate (admission-access S1): a member without VIEW_ADMISSION sees the
 * in-page no-access panel and the desk below never mounts, so it fires no
 * admission request (and no flash while permissions load). A member whose slug
 * is removed while the desk is open gets the "access changed" variant.
 */
export default function AdmissionDeskPage() {
  return (
    <NoAccessGate
      slug={ADMISSION_PERMISSIONS.VIEW_ADMISSION}
      deniedVariant={NO_ACCESS_VARIANT.ADMISSION}
      removedVariant={NO_ACCESS_VARIANT.CHANGED}
    >
      <AdmissionDesk />
    </NoAccessGate>
  );
}

/**
 * AdmissionDeskPage (S5 · C6a "Manage Admission") — the operational admissions
 * list. Filter state lives in the URL (B4 restore-on-return); the container loads
 * exactly ONE status partition per status change (stable `load`, effect keyed on
 * the primitive status → no request storm) — except "With dues" (R1), which
 * union-loads every dues-eligible terminal status (DUES_ELIGIBLE_STATUSES) in one
 * bounded Promise.all so a death-with-dues row is never invisible — and does every
 * other filter + the search CLIENT-SIDE over that slice (`applyDeskFilters`).
 * Cross-status search suggestions come from the lazily-warmed, single-flight
 * `useAdmissionSearchIndex`.
 *
 * Header count = the composed-filter length: the TRUE total of the loaded slice.
 * Known limit — a status partition is capped at the server's `_LIST_LIMIT` (500);
 * lists longer than that need D2 pagination. NO payment UI (dues = C6b).
 */
function AdmissionDesk() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // admission-access (MOCK-4) — the money filters (With dues / Low deposit) are
  // for a viewer who holds BOTH the bill and the IPD-dashboard slugs (permission,
  // never role): every doctor (primary included) and the nurse lack them, since
  // those counts would include rows the viewer may not see. The SAME boolean
  // drives the filter bar AND the parsed filter state — a `?filter=with_dues` /
  // `low_deposit` deep link is neutralised, never loaded and filtered to nothing.
  const canViewBill = usePermission(CHARGE_PERMISSIONS.VIEW_RUNNING_BILL);
  const canViewDashboard = usePermission(REPORT_PERMISSIONS.VIEW_IPD_DASHBOARD);
  const moneyFiltersAllowed = canViewBill && canViewDashboard;
  const filterState = useMemo(() => {
    const parsed = parseDeskParams(searchParams);
    return moneyFiltersAllowed ? parsed : withoutMoneyFilters(parsed);
  }, [searchParams, moneyFiltersAllowed]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // True when ANY loaded partition's read hit its row cap (the active view
  // may be missing rows). Additive/optional server field — see admissionApi
  // `listAdmissions` + the shared `anyPartitionTruncated` derivation.
  const [truncated, setTruncated] = useState(false);
  const [dialog, setDialog] = useState(null); // { kind, admission }
  const [selectedId, setSelectedId] = useState(null); // rail selection → workspace pin
  // A search pick that is NOT in the loaded rail (any-age discharge, CANCELLED
  // beyond the list cap, no discharge date): the admission fetched by id, so the
  // detail pane opens regardless of the rail window. `selected` prefers the rail row.
  const [pickedRow, setPickedRow] = useState(null);
  // The ONE pick lifecycle (search pick / post-admit highlight / `?admission=`
  // probe) — driven only through the pure `pickTransition` (deskPick.js). Only
  // a LIVE pick (loading | awaitingRow) may move the pane; every terminal phase
  // ignores later loads, so a refresh can never jump the pane (bk-1779).
  const pickRef = useRef(PICK_IDLE);
  // S2/D4 · FE-A — view mode. 'dashboard' = default no-patient glance pane
  // (AdmissionDashboardPanel); 'patient' = the pinned AdmissionWorkspace.
  // This is the SAME state block FE-B (S3 maximize) extends with a sibling
  // `maximized` flag — do not split view/maximized across two blocks.
  const [view, setView] = useState('dashboard');
  // S3/FE-B — maximize flag, sibling to view/selectedId (pure FE, no fetch).
  // true only while view === 'patient'; Close resets it, "back to split"
  // clears it without touching selectedId/view.
  const [maximized, setMaximized] = useState(false);
  const [shown, setShown] = useState(RAIL_PAGE_SIZE); // rail pagination
  // Rail sort control (DESK-IP-SORT) — page-local, NOT URL-persisted (the
  // bucket/status/search filters already own the URL restore contract; sort
  // is a display-order preference, not a narrowing filter). Defaults to the
  // SAME 'ip_asc' default `sortDeskRows` itself falls back to — single
  // source via `defaultDeskFilters().sortBy`, never a second hardcoded literal.
  const [sortBy, setSortBy] = useState(defaultDeskFilters().sortBy);
  const [notice, setNotice] = useState(null);
  // admission-access S7-AC9 — the id opened by deep link/highlight belongs to
  // another department: the pane shows the panel, never the admission.
  const [outOfScope, setOutOfScope] = useState(false);
  // An in-scope id opened by `?admission=` (shape = the highlight state it joins).
  const [probedOpen, setProbedOpen] = useState(null);
  const probeStarted = useRef(false);

  // S7-AC5 — the desk doctor filter is the server-served assignable list (the
  // department wall is applied server-side); one request per session, shared with
  // every other picker. The org-wide roster stays for names + the doctor→dept
  // edges the filter bar narrows by (its result is not a filter source any more).
  const rosterDoctors = useDoctorRoster();
  const { providers: assignableDoctors, loading: doctorsLoading, error: doctorsError } = useAssignableProviders(STAFF_ROLES.DOCTOR);
  const doctors = useMemo(() => mapDoctorsResponse(assignableDoctors), [assignableDoctors]);
  // Doctors on the rows the viewer can see (primary + care team) — the walled
  // desk adds them to the dropdown; computed locally, ZERO new requests.
  const rowDoctors = useMemo(() => collectRowDoctors(rows, rosterDoctors), [rows, rosterDoctors]);
  // S7-AC4 — the list response says whether the department wall applied to this
  // viewer (`dept_scoped`, additive; absent = not walled). Kept from every load;
  // drives the Department filter here and the transfer confirm line in B-FE-2.
  const [deptScoped, setDeptScoped] = useState(false);
  const [serverBlocked, setServerBlocked] = useState(null); // null = server hasn't said
  const deptAccessBlocked = useDeptAccessBlocked(serverBlocked);
  const searchIndex = useAdmissionSearchIndex();
  const serverSearch = useAdmissionServerSearch(filterState.q);
  const searchInputRef = useRef(null);
  const highlightHandled = useRef(false);
  const noticeTimer = useRef(null);

  // S12 (day-1 ADDENDUM-3) / RBAC-IPD FE-1 — "Today's IPD report" toolbar
  // entry, PERMISSION-gated (VIEW_IPD_REVENUE) — ADMIN always has it via
  // FULL_ACCESS; a receptionist on an IPD-on org is granted it server-side.
  const canViewDepositsReport = usePermission(REPORT_PERMISSIONS.VIEW_IPD_REVENUE);
  const canViewOccupancy = usePermission(ADMISSION_PERMISSIONS.VIEW_OCCUPANCY_BOARD);
  const [depositsReportOpen, setDepositsReportOpen] = useState(false);

  const flash = useCallback((msg) => {
    setNotice(msg);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 3200);
  }, []);
  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); }, []);

  // Stable (flash is stable; everything else is a setter or a ref) — no state,
  // no effect. Returns nothing; callers read `pickRef.current` after it.
  const dispatchPick = useCallback((ev) => {
    const r = pickTransition(pickRef.current, ev);
    pickRef.current = r.pick;
    if (r.open) {
      setSelectedId(r.open.id);
      setView('patient');
      setOutOfScope(false);
      if (r.open.row) setPickedRow(r.open.row);
    }
    if (r.pickedRow) setPickedRow(r.pickedRow);
    if (r.outOfScope) setOutOfScope(true);
    // Highlight / probe failures stay silent (as before); a search pick says so.
    if (r.notice === 'failed' && r.pick.source === 'search') flash("Couldn't open this admission. Try again.");
  }, [flash]);

  // ONE bounded Promise.all per bucket/raw-status change (S3 AC1 — extends the
  // "With dues" union-load precedent to every bucket): `bucketPartitions`
  // resolves the ACTIVE view to its N status partitions (Currently admitted: 4;
  // Discharged: 3; With dues: 2; a raw More… status: 1). `load` is stable and
  // the effect depends ONLY on the primitive `partitionsKey` string — a
  // sub-chip narrowing (client-side) or any re-render can never refire it.
  //
  // discharge-perf F2 — the plain Discharged bucket instead loads via ONE
  // `listDischarges` call for the IST discharge-date window `dfrom|dto`
  // (both blank ⇒ server default, last 30 days). The effect key is the
  // PRIMITIVE `D:${dfrom}|${dto}`, so dept/doctor/q/sort never refetch.
  const dischargeView = isDischargeWindowView(filterState);
  const loadKey = loadKeyOf(filterState);
  // Server-echoed resolved window (Discharged view only; null elsewhere).
  const [period, setPeriod] = useState(null);
  // bk-1865 — backdated completions from the SAME listDischarges response.
  const [backdatedToday, setBackdatedToday] = useState([]);
  // Single-flight: an identical window already in flight is shared (e.g. the
  // StrictMode double effect), never a second request. `fresh` (post-write
  // refetch) always issues a new one.
  const dischargeInflight = useRef(null);
  // Latest-wins: a slow earlier load (e.g. Discharged → Currently admitted
  // mid-flight) can never overwrite the rows of the view now showing.
  const loadSeq = useRef(0);
  // Seq of the latest COMPLETED load; a load is in flight ⇔ it differs from
  // loadSeq. The latest load's completion (success or error) settles a live pick.
  const loadDoneSeq = useRef(0);
  const settlePick = useCallback((seq, ok, list) => {
    loadDoneSeq.current = seq;
    dispatchPick({ type: 'ROWS_LOADED', seq, ok, ids: new Set(ok ? list.map((r) => r.admission_id) : []) });
  }, [dispatchPick]);
  const load = useCallback(async (f, { fresh = false } = {}) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    if (isDischargeWindowView(f)) {
      const key = `${f.dfrom}|${f.dto}`;
      let promise;
      if (!fresh && dischargeInflight.current && dischargeInflight.current.key === key) {
        promise = dischargeInflight.current.promise;
      } else {
        promise = listDischarges({ from: f.dfrom, to: f.dto });
        const entry = { key, promise };
        dischargeInflight.current = entry;
        promise.then(() => {
          if (dischargeInflight.current === entry) dischargeInflight.current = null;
        });
      }
      const res = await promise;
      if (seq !== loadSeq.current) return;
      if (!res.success) {
        setError(res.error || 'Failed to load admissions.');
        setTruncated(false);
      } else {
        setRows(res.admissions);
        setPeriod(res.period);
        setBackdatedToday(res.backdated_completed_today || []);
        setDeptScoped(res.dept_scoped === true);
        setServerBlocked(typeof res.dept_access_blocked === 'boolean' ? res.dept_access_blocked : null);
        setTruncated(!!(res.coverage && res.coverage.truncated));
      }
      setLoading(false);
      settlePick(seq, !!res.success, res.admissions || []);
      return;
    }
    const parts = bucketPartitions(f);
    const results = await Promise.all(parts.map((s) => listAdmissions({ status: s })));
    if (seq !== loadSeq.current) return;
    setPeriod(null);
    setBackdatedToday([]);
    const failed = results.find((r) => !r.success);
    const loaded = failed ? [] : results.flatMap((r) => r.data.admissions);
    if (failed) setError(failed.error || 'Failed to load admissions.');
    else {
      setRows(loaded);
      setDeptScoped(results.some((r) => r.data.dept_scoped === true));
      setServerBlocked(results.some((r) => r.data.dept_access_blocked === true) ? true
        : (results.every((r) => r.data.dept_access_blocked === false) ? false : null));
    }
    setTruncated(anyPartitionTruncated(results));
    setLoading(false);
    settlePick(seq, !failed, loaded);
  }, [settlePick]);

  useEffect(() => {
    load(filterState);
    // loadKey is the primitive projection of filterState that determines what
    // to load (status partitions, or the discharge window); other filterState
    // fields (doctor/dept/q/…) are applied client-side and must NOT retrigger
    // a network fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey, load]);

  // S2 AC4 — balance moves live: patch the matching row from the written WS
  // payload (no refetch, no storm — event-driven). The handler is stable via a
  // functional setRows update, so re-renders can't re-subscribe/re-fire.
  const canCollectDeposit = usePermission(PAYMENT_PERMISSIONS.COLLECT_DEPOSIT);
  const canPrintReceipt = usePermission(PAYMENT_PERMISSIONS.GENERATE_RECEIPT_PDF);
  const canGrantConcession = usePermission(CHARGE_PERMISSIONS.GRANT_CONCESSION);
  // O1 (retired) — Payment Pending / collect-dues-later is no longer offered;
  // discharge now hard-blocks until settled (write-off is the exception path,
  // C6b · FE-5, unchanged). WRITE_OFF_DUES still gates the write-off dialog.
  const canWriteOffDues = usePermission(DUES_PERMISSIONS.WRITE_OFF_DUES);
  // C2 — a monotonic token bumped once per successful lab order (OrderLabDialog's
  // `onOrdered`, called exactly once on all-success). An integer primitive in
  // a dep array = one extra fetch per order, threaded to AdmissionWorkspace →
  // DiagnosticsCard / RunningBillPanel / SettleCreditsCard. Never routed
  // through `afterAction` — that closes the dialog and reloads the whole desk
  // bucket, and the collect-now receipt path deliberately keeps the dialog
  // open. Never used as a `key` — a key change would remount the workspace
  // and tear down an open dialog inside the bill panel.
  const [diagnosticsRefreshToken, setDiagnosticsRefreshToken] = useState(0);
  // AGE-FE (bk-1292) — the SAME "sibling dialog bumps a monotonic counter"
  // idiom as `diagnosticsRefreshToken` above, so the pinned workspace's
  // Discharge Summary tab re-fetches (exactly once) after an age edit on
  // the "Patient Details" dialog — that GET is the only place age/sex is
  // shown on the Summary tab, and it has no other reason to reload when a
  // SIBLING dialog, not itself, changes the patient record.
  const [patientDetailsRefreshToken, setPatientDetailsRefreshToken] = useState(0);
  useRealtimeEvent(ADMISSION_BALANCE_EVENT, useCallback((event) => {
    const payload = event?.payload || event;
    setRows((cur) => patchAdmissionBalance(cur, payload));
  }, []));

  // Live row-patch after an in-workspace money mutation (discharge collect /
  // return-advance / free-form refund / add-charge / reverse / concession).
  // Every such response already carries the post-write balance, so we patch the
  // parent row from it through the SAME single-source `patchAdmissionBalance`
  // seam the WS event uses — no refetch. This is what keeps the KPI strip /
  // deposit-low banner / rail chip live AND flips the RunningBillPanel
  // remount-key so the ledger refetches once. Stable ([] deps, functional
  // setRows) so a re-render can never re-fire it or a fetch.
  const onAdmissionPatched = useCallback((patch) => {
    setRows((cur) => patchAdmissionBalance(cur, patch));
  }, []);

  // Update filters → serialize to the URL (single source of truth for restore).
  const setFilters = useCallback((next) => {
    setSearchParams(serializeDeskParams(next), { replace: true });
  }, [setSearchParams]);

  const filtered = useMemo(
    () => sortDeskRows(applyDeskFilters(rows, filterState), { ...filterState, sortBy }),
    [rows, filterState, sortBy],
  );

  // Rail pagination resets to the first page on ANY filter/search change. Keyed
  // on a PRIMITIVE serialized signature (not the filterState object, which is
  // rebuilt each render) so a re-render can't reset the page mid-scroll (same
  // stable-primitive discipline as the load effect's `partitionsKey`).
  const filterSig = useMemo(() => serializeDeskParams(filterState).toString(), [filterState]);
  useEffect(() => { setShown(RAIL_PAGE_SIZE); }, [filterSig]);

  // S2/D4 — the workspace pins to the rail-selected admission ONLY while
  // `view === 'patient'`; it no longer auto-falls-back to `filtered[0]`, so
  // the S2 dashboard (view === 'dashboard') is reachable even with rows
  // present. If the pinned row falls out of the current filter, `selected`
  // resolves to null and the pane renders the dashboard (no dangling pin).
  // Derived, no effect, so it can't loop.
  const selected = useMemo(
    () => {
      if (view !== 'patient') return null;
      const inRail = filtered.find((r) => r.admission_id === selectedId);
      if (inRail) return inRail;
      return pickedRow && pickedRow.admission_id === selectedId ? pickedRow : null;
    },
    [filtered, selectedId, view, pickedRow],
  );
  const railSlice = useMemo(() => filtered.slice(0, shown), [filtered, shown]);

  // S2 AC6 — ONE shared count derivation over the loaded bucket + (if already
  // warmed) the cross-status search index; never triggers a fetch itself.
  const counts = useMemo(
    () => computeDeskCounts(rows, searchIndex.rows, filterState),
    [rows, searchIndex.rows, filterState],
  );

  // Post-admit highlight (from AdmitPatientPage success nav): switch to the
  // bucket-or-raw view that HOMES the new admission's status (S1
  // `bucketForStatus`, via `viewForStatus`) if it isn't already visible, then
  // expand + scroll its row.
  useEffect(() => {
    if (highlightHandled.current) return;
    const st = (location.state && location.state.highlightAdmissionId) ? location.state : probedOpen;
    if (!st || !st.highlightAdmissionId) return;
    highlightHandled.current = true;
    const id = st.highlightAdmissionId;
    const targetView = st.admissionStatus && !bucketPartitions(filterState).includes(st.admissionStatus)
      ? viewForStatus(st.admissionStatus)
      : null;
    const switchTo = targetView && withoutStaleDischargeWindow({ ...filterState, ...targetView, q: '' });
    const before = pickRef.current;
    dispatchPick({
      // A `?admission=` probe answer carries the pick token it started under:
      // if the user acted since, it is ignored (T20, bk-1782).
      ...(st.probeToken === undefined ? { type: 'HIGHLIGHT' } : { type: 'PROBE_IN_SCOPE', startToken: st.probeToken }),
      id,
      inRows: rows.some((r) => r.admission_id === id),
      inFlight: loadSeq.current !== loadDoneSeq.current,
      needsReload: !!switchTo && loadKeyOf(switchTo) !== loadKeyOf(filterState),
      loadSeq: loadSeq.current,
    });
    if (pickRef.current === before) return;
    if (switchTo) {
      setFilters(switchTo);
      const label = targetView.bucket ? DESK_BUCKET_LABELS[targetView.bucket] : (ADMISSION_STATUS_LABELS[st.admissionStatus] || st.admissionStatus);
      flash(`Showing ${label}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, probedOpen, filterState, setFilters, flash, dispatchPick]);

  // admission-access S7-AC9 — direct open by id (`?admission=` deep link, or the
  // highlight state). The server-scoped list never contains a foreign admission,
  // so the list alone can't tell "not yours" from "not there": ONE getAdmission
  // probe per mount decides (a ref guards the StrictMode double-effect). 403
  // OUT_OF_DEPARTMENT_SCOPE → the panel; an in-scope URL id joins the highlight
  // path above (its `status` picks the view that homes the row).
  useEffect(() => {
    if (probeStarted.current) return;
    const fromUrl = searchParams.get(OPEN_ADMISSION_PARAM);
    const id = fromUrl || (location.state && location.state.highlightAdmissionId);
    if (!id) return;
    probeStarted.current = true;
    const probeToken = pickRef.current.token;
    getAdmission(id).then((res) => {
      if (res.success) {
        if (fromUrl) setProbedOpen({ highlightAdmissionId: id, admissionStatus: res.data && res.data.status, probeToken });
      } else if (res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE) {
        dispatchPick({ type: 'PROBE_OUT_OF_SCOPE', startToken: probeToken });
      }
    });
    // Mount-only by design: the id is read once, never re-probed on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local (rail + warm-up index) matches render instantly; server rows (org-wide,
  // any age) merge in behind them, deduped by admission_id (local copy wins).
  const suggestions = useMemo(
    () => mergeSearchResults(
      matchSuggestions(mergeSuggestionRows(rows, searchIndex.rows), filterState.q, Infinity).items,
      serverSearch.rows,
    ),
    [rows, searchIndex.rows, filterState.q, serverSearch.rows],
  );

  const onSearchChange = useCallback((q) => {
    setFilters({ ...filterState, q });
  }, [filterState, setFilters]);

  const onSearchFocus = useCallback(() => {
    // Warm the non-terminal index on every focus — single-flight + cached, so a
    // warm index is a free no-op and a post-action invalidate re-warms once.
    searchIndex.warm();
  }, [searchIndex]);

  const onPickSuggestion = useCallback((a) => {
    const targetView = viewForStatus(a.status);
    // A department/doctor filter that would hide the picked row is relaxed (and
    // named in the notice) so the pick opens instead of silently doing nothing.
    const hiding = filtersHidingPick(filterState, a);
    let patch = { ...filterState, ...targetView, ...hiding.patch, q: '' };
    // Any change of view (bucket, raw status, With dues, sub-chip) is announced.
    const switching = ['bucket', 'rawStatus', 'withDuesOnly', 'subchip', 'lowDepositOnly']
      .some((k) => (filterState[k] || '') !== (patch[k] || ''));
    // discharge-perf F4 — a pick that lands on the Discharged window view but
    // falls OUTSIDE the current dfrom/dto (e.g. found via search, discharged
    // months ago) must not resolve to an empty rail: widen the window to
    // include it, capped at the D1 3-month span (single source, `deskFilters`).
    if (isDischargeWindowView(patch)) {
      patch = { ...patch, ...anchorDischargeWindow(patch, a) };
    } else {
      patch = withoutStaleDischargeWindow(patch);
    }
    const inRows = rows.some((r) => r.admission_id === a.admission_id);
    // needsReload / settleSeq are read BEFORE setFilters bumps loadSeq.
    dispatchPick({
      type: 'PICK',
      id: a.admission_id,
      inRows,
      needsReload: loadKeyOf(patch) !== loadKeyOf(filterState),
      inFlight: loadSeq.current !== loadDoneSeq.current,
      loadSeq: loadSeq.current,
    });
    const { token } = pickRef.current;
    setFilters(patch);
    // A pick must never do nothing: a row the rail doesn't hold (older than the
    // window, no discharge date, beyond the list cap) opens from its own fetch.
    if (switching || !inRows) {
      getAdmission(a.admission_id)
        .catch(() => ({ success: false }))
        .then((res) => dispatchPick({ type: res.success && res.data ? 'GET_OK' : 'GET_FAIL', token, row: res.data }));
    }
    const notes = [];
    if (switching) {
      const label = targetView.bucket ? DESK_BUCKET_LABELS[targetView.bucket] : (ADMISSION_STATUS_LABELS[a.status] || a.status);
      notes.push(`Switched to ${label}`);
    }
    if (hiding.cleared.length) notes.push(`Cleared the ${hiding.cleared.join(' and ')} filter`);
    if (notes.length) flash(notes.join(' · '));
  }, [filterState, rows, setFilters, flash, dispatchPick]);

  // S2 AC8 / S5 AC4 — "Not here? Search all patients": clear every filter AND
  // focus the search box (one shared component/handler, not two copies).
  const onSearchAllPatients = useCallback(() => {
    setFilters({});
    if (searchInputRef.current && searchInputRef.current.focus) searchInputRef.current.focus();
  }, [setFilters]);

  const onSelectRail = useCallback((id) => {
    dispatchPick({ type: 'CANCEL' }); // a live pick must never take over this selection
    setSelectedId(id);
    setView('patient');
    setOutOfScope(false);
  }, [dispatchPick]);
  // S2/D4 — deselect path back to the dashboard. FE-B's "Close" control
  // (S3) calls this same handler; kept here so both land on ONE seam. Also
  // resets maximized — Close always lands on the RESTING dashboard, never a
  // maximized-but-empty state.
  const onDeselect = useCallback(() => {
    dispatchPick({ type: 'CANCEL' });
    setSelectedId(null);
    setView('dashboard');
    setMaximized(false);
    setOutOfScope(false);
  }, [dispatchPick]);
  // S3 — maximize/restore. "Back to split view" un-maximizes WITHOUT
  // deselecting (selectedId/view untouched, rail returns).
  const onMaximize = useCallback(() => setMaximized(true), []);
  const onBackToSplit = useCallback(() => setMaximized(false), []);
  const onShowMore = useCallback(() => {
    setShown((n) => n + RAIL_PAGE_SIZE);
  }, []);

  const openDialog = useCallback((kind, admission) => setDialog({ kind, admission }), []);
  const closeDialog = () => setDialog(null);

  // Action success: refetch the ACTIVE view (bucket partitions, or the SAME
  // discharge window — never widened/reset), KEEP the expanded
  // row open (keyed by admission_id), invalidate the search index so the next
  // warm re-fetches.
  // ONE seam for every write-driven desk refresh: marks the warm-up index AND
  // the 60 s server-search cache stale, then refetches the active view.
  const refreshAfterWrite = () => {
    searchIndex.invalidate();
    serverSearch.retry();
    return load(filterState, { fresh: true });
  };
  const afterAction = async (res) => {
    if (res && res.success) {
      closeDialog();
      await refreshAfterWrite();
    } else if (res && res.code === ADMISSION_ACCESS_ERROR_CODE.CARE_TEAM_CONFLICT) {
      // Ratified #4: "The list is now up to date" is only true after a refetch,
      // so reload BEFORE the dialog shows it (dialog stays open).
      await load(filterState, { fresh: true });
    }
    return res;
  };

  const onExport = useCallback(() => {
    // discharge-perf F5 (D6a) — `period` is the server-echoed window (null
    // outside the Discharged view), so the filename carries the range there
    // and stays untouched everywhere else.
    downloadAdmissionsCsv(filtered, period);
  }, [filtered, period]);

  // QA-FE-3 (fixed), pre-PR review SHOULD-FIX — window empty-state phrase,
  // built from the ONE shared `dischargeWindowPhrase` (never a second
  // period->words mapping; was duplicated string-surgery on the pill label).
  const dischargeEmptyPhrase = (dischargeView && period && dischargeWindowPhrase(period)) || 'in the chosen dates';

  if (deptAccessBlocked) return <DeptAccessBlockedPanel />;

  const admitState = { returnTo: location.pathname + location.search };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <AdminPageHeader />
      <div className={`${maximized ? 'max-w-[1800px]' : 'max-w-6xl'} mx-auto p-4`} data-testid="admission-desk">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Manage Admission</h1>
            {/* S13 — the server's `dept_scoped` word; names the walled viewer's scope. */}
            <ScopeCaption scoped={deptScoped} />
          </div>
          <span
            className="text-[12px] font-semibold px-2.5 py-0.5 rounded-full border border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200"
            data-testid="desk-count"
            data-period-from={dischargeView && period ? period.from : undefined}
            data-period-to={dischargeView && period ? period.to : undefined}
          >
            {/* D3 (owner-decisions.md, LOCKED) — "{n} discharged" on the
                Discharged view; every other view keeps "{n} admission(s)".
                Reads the SAME `filtered.length` the rail/export already use
                (no second count derivation), just a different label.
                QA-FE-3 (fixed) — the windowed view additionally names the
                SERVER-echoed period via the ONE shared `dischargeWindowLabel`
                (never a client recompute): "today" / "last 30 days" / a
                short range. PO/Business ratify final wording at click-through. */}
            {filterState.bucket === DESK_BUCKET.DISCHARGED && !filterState.withDuesOnly
              ? (dischargeView && period
                  ? `${filtered.length} discharged · ${dischargeWindowLabel(period)}`
                  : `${filtered.length} discharged`)
              : `${filtered.length} admission${filtered.length === 1 ? '' : 's'}`}
          </span>
          <Link
            to={ROUTES.ADMIT_PATIENT}
            state={admitState}
            className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-medium"
            data-testid="admit-link"
          >
            + Admit
          </Link>
          {/* S12 (day-1 ADDENDUM-3) / RBAC-IPD FE-1 — one-click "Today's IPD
              report" entry, gated on VIEW_IPD_REVENUE (ADMIN always has it;
              a granted receptionist now sees it too). */}
          {canViewDepositsReport && (
            <button
              type="button"
              onClick={() => setDepositsReportOpen(true)}
              data-testid="toolbar-ipd-deposits-report"
              className="px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              Today&rsquo;s IPD report
            </button>
          )}
          {/* Occupancy board entry — secondary style (+ Admit stays the one
              primary), icon-only below 560px, hidden without the permission.
              returnTo lets the board's Back restore this desk's URL filters. */}
          {canViewOccupancy && (
            <Link
              to={ROUTES.OCCUPANCY_BOARD}
              state={admitState}
              aria-label="Occupancy board"
              title="Occupancy board"
              data-testid="toolbar-occupancy-board"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M3 18V7M3 14h18v4M21 14v-2a3 3 0 0 0-3-3h-7v5" />
                <circle cx="7" cy="11" r="1.5" />
              </svg>
              <span className="max-[559px]:hidden">Occupancy board</span>
            </Link>
          )}
          <SearchSuggestInput
            ref={searchInputRef}
            value={filterState.q}
            onChange={onSearchChange}
            suggestions={suggestions}
            onFocus={onSearchFocus}
            onPick={onPickSuggestion}
            busy={searchIndex.warming}
            server={serverSearch}
          />
        </div>

        {notice && (
          <div className="mb-3 rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[12.5px] p-2.5" data-testid="desk-notice">{notice}</div>
        )}

        {/* Sticky toolbar (R10 long-list rule) — chips + secondary filters stay
            pinned so re-filtering never requires scrolling back up. Maximized
            HIDES the toolbar (same pattern as the rail below); filterState is
            URL-driven (see parseDeskParams(searchParams) above) so it persists
            and re-binds unchanged when un-maximized. */}
        {!maximized && (
          <div className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900 pt-1 pb-1 -mx-4 px-4">
            <DeskFilterBar
              filterState={filterState}
              onChange={setFilters}
              doctors={doctors}
              rowDoctors={rowDoctors}
              rosterDoctors={rosterDoctors}
              doctorsLoading={doctorsLoading}
              doctorsError={!!doctorsError}
              deptScoped={deptScoped}
              onExport={onExport}
              exportDisabled={filtered.length === 0}
              counts={counts}
              showMoneyFilters={moneyFiltersAllowed}
              backdatedCompleted={backdatedToday}
            />
          </div>
        )}

        {/* C3 — bed-day accrual visibility (silent failure impossible). */}
        <div className="my-3"><AccrualRunBanner /></div>

        {/* Incomplete-list warning — ANY loaded partition hit its row cap
            (additive/optional `truncated` field, defaults false). Never shows
            when false/absent (own testcase); solid day+dark backgrounds. */}
        {truncated && (
          <div className="mb-3 rounded-lg border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-[12.5px] p-2.5" data-testid="desk-truncated-banner">
            {/* QA-FE-3 (fixed) — the windowed view's fix is to narrow the
                DATES (search can't find a row that fell outside the window
                the server never returned); every other (non-windowed) view
                keeps the original "search by name" guidance, unchanged.
                PO/Business ratify final wording at click-through. */}
            {dischargeView
              ? 'This list is incomplete — narrow the dates to see everyone.'
              : 'This list is incomplete — some patients are not shown. Search by name above to find a patient who is missing.'}
          </div>
        )}

        {error && <div className="mb-3 rounded-lg border border-red-300 bg-red-50 text-red-800 text-[12.5px] p-2.5" data-testid="desk-error">{error}</div>}

        {/* Split-pane workspace (UX-1 Variant B): a searchable left rail
            (always-visible long list) + a right workspace pinned to the
            selected admission. S3/FE-B — maximized HIDES the rail entirely
            (the workspace's flex-1 then fills the freed width; no extra
            width class needed on the workspace itself).

            FIX-5-FE — `calc(100vh - 260px)` used to be this row's actual
            fixed HEIGHT with `items-stretch`, so every tab's content (Bill,
            Summary, ...) was force-clipped to that box regardless of how
            many fields/lines it had — the Discharge Summary tab's 16 fields
            plus a sticky footer only left room for ~1 row visible. Now it's
            a MIN-height floor (no `height`), `items-start` so children no
            longer stretch to match each other, and the rail restates the
            same old height explicitly (below) so IT renders pixel-identical
            to before. The row's own rendered height is then
            `max(rail's fixed height, workspace's natural content height)`
            — unchanged for short tabs (rail sets the floor, same as
            today), and grows for a tall one so the PAGE scrolls instead of
            the pane clipping it. */}
        <div className="flex gap-3 items-start mt-3" style={{ minHeight: 'max(calc(100vh - 260px), 480px)' }} data-testid="desk-split">
          {/* Left rail — explicit height (not inherited via `items-stretch`
              any more) so its own size/scroll is byte-identical to before,
              independent of how tall the workspace column grows. */}
          {!maximized && (
            <div
              className="w-[340px] flex-none flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden"
              style={{ height: 'calc(100vh - 260px)', minHeight: '480px' }}
              data-testid="desk-rail"
            >
              <div className="flex-none flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Patients</span>
                <select
                  className="px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-teal-500"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort"
                  data-testid="desk-sort"
                >
                  <option value="ip_asc">IP number — low to high</option>
                  <option value="ip_desc">IP number — high to low</option>
                  <option value="ward">Ward &amp; bed</option>
                </select>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400 p-4" data-testid="desk-loading">Loading admissions…</p>
                ) : filtered.length === 0 ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400 p-6 text-center" data-testid="desk-empty">
                    <p className="mb-1">
                      {/* Copy-deck §6 — per-filter empty states; a live search
                          query shows the ratified "No matches." (AC5). */}
                      {/* QA-FE-3 (fixed) — the windowed empty state names the
                          SERVER-echoed period (built from the ONE shared
                          `dischargeWindowLabel`) instead of the old "yet",
                          which read as "nobody has EVER been discharged". */}
                      {/* Business verdict R1 (2026-09-30) — a name search
                          inside the Discharged window that finds nothing
                          names the searched window and how to widen it
                          (bk-512's user-facing symptom); every other view
                          (incl. Discharged with no `q`) keeps the plain
                          "No matches."/its own copy, unchanged.
                          PO/Business ratified 2026-09-30. */}
                      {filterState.q
                        ? (dischargeView && period && dischargeSearchEmptyHint(period)) || 'No matches.'
                        : filterState.withDuesOnly
                          ? 'No admissions with recorded dues.'
                          : dischargeView
                            ? `No one was discharged ${dischargeEmptyPhrase}.`
                            : 'No admissions in this view.'}
                    </p>
                    <button
                      type="button"
                      onClick={onSearchAllPatients}
                      className="text-teal-700 dark:text-teal-300 font-semibold underline"
                      data-testid="empty-search-all"
                    >
                      Not here? Search all patients
                    </button>
                  </div>
                ) : (
                  <div data-testid="desk-rows">
                    {railSlice.map((a) => (
                      <AdmissionRailItem
                        key={a.admission_id}
                        admission={a}
                        active={selected && selected.admission_id === a.admission_id}
                        onSelect={onSelectRail}
                      />
                    ))}
                  </div>
                )}
              </div>
              {!loading && filtered.length > 0 && (
                <div className="flex-none border-t border-slate-200 dark:border-slate-700 p-2 text-center">
                  {shown < filtered.length && (
                    <button
                      type="button"
                      onClick={onShowMore}
                      data-testid="rail-show-more"
                      className="w-full px-3 py-1.5 rounded-lg text-[12.5px] font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Show {Math.min(RAIL_PAGE_SIZE, filtered.length - shown)} more
                    </button>
                  )}
                  <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1.5" data-testid="rail-foot">
                    Showing {Math.min(shown, filtered.length)} of {filtered.length}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Right workspace — FIX-5-FE: no `overflow-hidden`/fixed height
              here any more (see `desk-split` note above) so a tall tab
              (Discharge Summary, a long Bill) is never clipped; it grows
              naturally and the page scrolls. */}
          <div className="flex-1 min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" data-testid="desk-workspace">
            {outOfScope ? (
              <div className="p-6">
                <NoAccessPanel variant={NO_ACCESS_VARIANT.OUT_OF_DEPARTMENT} onBack={onDeselect} />
              </div>
            ) : selected ? (
              <AdmissionWorkspace
                key={selected.admission_id}
                admission={selected}
                onAction={openDialog}
                onDischarged={refreshAfterWrite}
                onAdmissionPatched={onAdmissionPatched}
                onClose={onDeselect}
                maximized={maximized}
                onMaximize={onMaximize}
                onBackToSplit={onBackToSplit}
                diagnosticsRefreshToken={diagnosticsRefreshToken}
                patientDetailsRefreshToken={patientDetailsRefreshToken}
              />
            ) : (
              <AdmissionDashboardPanel />
            )}
          </div>
        </div>

        {/* Dialogs — reused verbatim from deskDialogs (C-3, no contract change). */}
        {dialog && dialog.kind === 'assignBed' && (
          <AssignBedDialog
            admission={dialog.admission}
            onClose={closeDialog}
            onSubmit={async (bedId) => {
              const res = await attachBed(dialog.admission.admission_id, { bed_id: bedId });
              if (res.success) await afterAction(res);
              return res;
            }}
          />
        )}
        {dialog && dialog.kind === 'transferBed' && (
          <TransferBedDialog
            admission={dialog.admission}
            onClose={closeDialog}
            onSubmit={(payload) => transferBed(dialog.admission.admission_id, payload).then(afterAction)}
          />
        )}
        {dialog && dialog.kind === 'orderLab' && (
          <OrderLabDialog
            admission={dialog.admission}
            onClose={closeDialog}
            onOrdered={() => setDiagnosticsRefreshToken((n) => n + 1)}
          />
        )}
        {dialog && dialog.kind === 'changeDoctor' && (
          <ChangeDoctorDialog
            admission={dialog.admission}
            deptScoped={deptScoped}
            onClose={closeDialog}
            onSubmit={(payload) => changePrimaryDoctor(dialog.admission.admission_id, payload).then(afterAction)}
          />
        )}
        {dialog && dialog.kind === 'attender' && (
          <AttenderDialog
            admission={dialog.admission}
            onClose={closeDialog}
            onSubmit={(payload) => patchAdmission(dialog.admission.admission_id, payload).then(afterAction)}
          />
        )}
        {dialog && dialog.kind === 'patientDetails' && (
          <PatientDetailsDialog
            admission={dialog.admission}
            onClose={closeDialog}
            onSaved={() => {
              setPatientDetailsRefreshToken((n) => n + 1);
              return afterAction({ success: true });
            }}
          />
        )}
        {dialog && dialog.kind === 'cancel' && (
          <CancelAdmissionDialog
            admission={dialog.admission}
            onClose={closeDialog}
            onSubmit={(payload) => transitionAdmission(dialog.admission.admission_id, payload).then(afterAction)}
          />
        )}
        {dialog && dialog.kind === 'editPlan' && (
          <EditPlanEstimateDialog
            admission={dialog.admission}
            onClose={closeDialog}
            onSubmit={(payload) => updatePlanEstimate(dialog.admission.admission_id, payload).then(afterAction)}
          />
        )}
        {dialog && dialog.kind === 'deposit' && canCollectDeposit && (
          <TakeDepositDialog
            admission={dialog.admission}
            balancePaise={dialog.admission.balance_paise}
            depositsHeldPaise={dialog.admission.deposit_total_paise}
            onClose={closeDialog}
            onSubmit={async (body) => {
              const res = await collectDeposit(dialog.admission.admission_id, body);
              if (res.success) await refreshAfterWrite();
              return res;
            }}
            onPrintReceipt={canPrintReceipt ? ((paymentId, opts) => openAdmissionReceiptPdf(dialog.admission.admission_id, paymentId, opts)) : undefined}
          />
        )}
        {dialog && dialog.kind === 'concession' && canGrantConcession && (
          <WaiverConcessionDialog
            admission={dialog.admission}
            balancePaise={dialog.admission.balance_paise}
            onClose={closeDialog}
            onSubmit={async (body) => {
              const res = await grantConcession(dialog.admission.admission_id, body);
              if (res.success) await refreshAfterWrite();
              return res;
            }}
          />
        )}
        {/* Write off dues (C6b · FE-5) — the ONE shared WriteOffDuesDialog
            mount, opened from the AdmissionRow/workspace detail
            (`dialog.admission` = the full admission row). Admin-only
            (WRITE_OFF_DUES); success removes the row from the desk list
            (write-off never partial — remainder-only) and raises the
            ratified toast. */}
        {dialog && dialog.kind === 'writeOffDues' && canWriteOffDues && (
          <WriteOffDuesDialog
            admission={dialog.admission}
            canWriteOff={canWriteOffDues}
            onClose={closeDialog}
            onSubmit={async (body) => {
              const res = await writeOffDues(dialog.admission.admission_id, body);
              if (res.success) {
                const admissionId = dialog.admission.admission_id;
                const patch = {
                  admission_id: admissionId,
                  dues_paise: res.data.dues_paise_after,
                  dues_status: res.data.dues_status_after,
                  dues_collected_paise: res.data.dues_collected_paise,
                  balance_paise: res.data.balance_paise_after,
                };
                setRows((cur) => patchAdmissionBalance(cur, patch));
                flash(`Due for ${dialog.admission.patient_name} written off.`);
              }
              return res;
            }}
          />
        )}
        {depositsReportOpen && (
          <IpdDepositsReportDialog onClose={() => setDepositsReportOpen(false)} />
        )}
      </div>
    </div>
  );
}
